import { GQLContentPageFragment, GQLContentPagePageComponentType, GQLContentPageRemoveSpacingType } from '@theprintbar/graphql';
import { BrandStarIcon, ButtonSize, Container, LinkTiles, RichText, TitleSectionProps } from '@theprintbar/ui';
import { Suspense, lazy } from 'react';
import { BigText } from '~/components/BigText';
import { CategoryFAQs } from '~/components/CategoryFAQs';
import { ContentTiles } from '~/components/ContentTiles';
import { FAQ } from '~/components/FAQ';
import { FlipTiles } from '~/components/FlipTiles';
import { HeroBanner } from '~/components/HeroBanner';
import { IconTiles } from '~/components/IconTiles';
import { InstagramCarousel } from '~/components/InstagramCarousel';
import { JournalCarousel } from '~/components/JournalCarousel';
import { KeyPoints } from '~/components/KeyPoints';
import { Marquee } from '~/components/Marquee';
import { Marquee2 } from '~/components/Marquee2';
import { Marquee3 } from '~/components/Marquee3';
import { OrderAndShippingTimes } from '~/components/OrderAndShippingTimes';
import { PlatformBlocks } from '~/components/PlatformBlocks';
import { PlatformCards } from '~/components/PlatformCards';
import { ProductCarousel } from '~/components/ProductCarousel';
import { ProductCarousel2 } from '~/components/ProductCarousel2';
import { ProductCarousel3 } from '~/components/ProductCarousel3';
import { StackedPills } from '~/components/StackedPills';
import { StarTiles } from '~/components/StarTiles';
import { getCTA } from '~/components/getCTA';

const ExampleDesigner = lazy(() => import('~/components/ExampleDesigner').then(m => ({ default: m.ExampleDesigner })));

export interface GetComponentProps {
	type: GQLContentPagePageComponentType;
	previousType?: GQLContentPagePageComponentType;
	nextType?: GQLContentPagePageComponentType;
	contentPage: GQLContentPageFragment;
	index: number;
}

export function getComponent(props: GetComponentProps) {
	const { type, contentPage, index } = props;
	let { titleContainer, ctaTitleSectionProps, component } = getCTA(props);
	let titleSectionProps: Partial<TitleSectionProps> = { ...ctaTitleSectionProps };

	titleSectionProps.removeSpacing = contentPage.removeSpacing?.includes(type as any as GQLContentPageRemoveSpacingType);

	//It's a CTA so we can just return it
	if (component) return { titleContainer, component, titleSectionProps };

	switch (type) {
		case GQLContentPagePageComponentType.BigText:
			component = <BigText content={contentPage} />;
			break;
		case GQLContentPagePageComponentType.ContentTiles:
			component = <ContentTiles content={contentPage} />;
			titleContainer = contentPage.contentTilesContainer;
			titleSectionProps.fullWidthChild = true;
			titleSectionProps.buttonSize = ButtonSize.L;
			titleSectionProps.buttonClass = 'group font-display font-light text-xl capitalize';
			titleSectionProps.buttonIcon = BrandStarIcon;
			titleSectionProps.iconClass = titleContainer.structure?.isPrimary
				? 'fill-none stroke-white'
				: 'fill-none stroke-blue group-hover:fill-none group-hover:stroke-white traisition-all duration-500';
			break;
		case GQLContentPagePageComponentType.Faq:
			component = <FAQ />;
			titleSectionProps.fullWidthChild = true;
			titleSectionProps.isButtonStacked = true;
			// lg:-mt-32 -mt-20 to match the design
			titleSectionProps.className = 'border-b border-blue bg-blue-bright/20 py-16 my-0 lg:my-0';
			titleContainer = contentPage.faqContainer;
			break;
		case GQLContentPagePageComponentType.FlipTiles:
			component = <FlipTiles content={contentPage} />;
			titleContainer = contentPage.flipTilesContainer;
			break;
		case GQLContentPagePageComponentType.HeroBanner:
			component = <HeroBanner content={contentPage} isHero={!index} />;
			break;
		case GQLContentPagePageComponentType.IconTiles:
			component = <IconTiles content={contentPage} />;
			break;
		case GQLContentPagePageComponentType.InstagramCarousel:
			titleContainer = contentPage.instagramCarouselContainer;
			titleSectionProps.fullWidthChild = true;
			titleSectionProps.carouselKey = 'InstagramCarousel';

			component = <InstagramCarousel carouselKey={titleSectionProps.carouselKey} />;

			break;
		case GQLContentPagePageComponentType.JournalCarousel:
			component = <JournalCarousel content={contentPage} />;
			break;
		case GQLContentPagePageComponentType.LinkTiles:
			component = <LinkTiles content={contentPage} />;
			break;
		case GQLContentPagePageComponentType.Marquee:
			component = <Marquee content={contentPage} />;
			break;
		case GQLContentPagePageComponentType.Marquee2:
			component = <Marquee2 content={contentPage} />;
			break;
		case GQLContentPagePageComponentType.Marquee3:
			component = <Marquee3 content={contentPage} />;
			break;
		case GQLContentPagePageComponentType.OrderAndShippingTimes:
			component = <OrderAndShippingTimes content={contentPage} />;
			titleContainer = contentPage.orderAndShippingTimesContainer;
			break;
		case GQLContentPagePageComponentType.ProductCarousel:
			titleContainer = contentPage.productCarouselContainer;
			titleSectionProps.carouselKey = 'ProductCarousel';
			titleSectionProps.fullWidthChild = true;

			component = <ProductCarousel carouselKey={titleSectionProps.carouselKey} />;

			break;
		case GQLContentPagePageComponentType.ProductCarousel2:
			titleContainer = contentPage.productCarouselContainer2;
			titleSectionProps.carouselKey = 'ProductCarousel2';
			titleSectionProps.fullWidthChild = true;

			component = <ProductCarousel2 carouselKey={titleSectionProps.carouselKey} />;

			break;
		case GQLContentPagePageComponentType.ProductCarousel3:
			titleContainer = contentPage.productCarouselContainer3;
			titleSectionProps.carouselKey = 'ProductCarousel3';
			titleSectionProps.fullWidthChild = true;

			component = <ProductCarousel3 carouselKey={titleSectionProps.carouselKey} />;

			break;
		case GQLContentPagePageComponentType.RichText:
			component = (
				<Container className="my-12 max-w-7xl">
					<RichText className="space-y-2" content={contentPage.richText} />
				</Container>
			);
			break;
		case GQLContentPagePageComponentType.StarTiles:
			component = <StarTiles content={contentPage} />;
			titleContainer = contentPage.starTilesContainer;
			titleSectionProps.fullWidthChild = true;
			break;
		case GQLContentPagePageComponentType.StackedPills:
			component = <StackedPills content={contentPage} />;
			break;
		case GQLContentPagePageComponentType.KeyPoints:
			component = <KeyPoints content={contentPage} />;
			break;
		case GQLContentPagePageComponentType.DesignTool:
			component = (
				<Suspense fallback={<div />}>
					<ExampleDesigner />
				</Suspense>
			);
			break;
		case GQLContentPagePageComponentType.PlatformBlocks:
			component = <PlatformBlocks content={contentPage} />;
			titleContainer = contentPage.platformBlocksContainer;
			titleSectionProps.fullWidthChild = true;
			break;
		case GQLContentPagePageComponentType.PlatformCards:
			component = <PlatformCards content={contentPage} />;
			titleContainer = contentPage.platformCardsContainer;
			titleSectionProps.fullWidthChild = true;
			break;
		case GQLContentPagePageComponentType.CategoryFaQs:
			component = <CategoryFAQs content={contentPage} />;
			break;
		default:
			component = <div>Unknown Component {type}</div>;
	}

	return {
		titleContainer,
		titleSectionProps,
		component,
	};
}
